import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
// UPPDRAG-41: NumberInput was the Nivå picker; both call sites removed.
// UPPDRAG-45: Select for the Speldag dropdown — replaces the free-form
// Input + datalist pattern. Stores canonical lowercase English keys
// (monday/.../sunday) that align with leagues.play_days[] and the rest
// of the codebase. Migration 20260502130000 normalized PROD data.
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Pencil, Plus, Shuffle, GripVertical, Clock, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useOrgHall } from "@/hooks/useOrgHall";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

interface AdminDivisionsProps {
  leagueId: string;
  maxTeamsPerDivision: number;
  promotionCount: number;
  relegationCount: number;
  // UPPDRAG-46: passed from parent so the "Matchtid" Select can show
  // the league default in its "Använd seriens inställning (X min)"
  // option. Per-division override (divisions.match_duration_minutes)
  // falls back to this when NULL.
  leagueMatchDurationMinutes: number;
}

// ---------- helpers ----------

function lastName(fullName: string | null | undefined): string {
  if (!fullName) return "?";
  const parts = fullName.trim().split(/\s+/);
  return parts[parts.length - 1];
}

function avgLevel(p1Level: number | null | undefined, p2Level: number | null | undefined): number {
  const l1 = p1Level ?? 1;
  const l2 = p2Level ?? 1;
  return Math.round(((l1 + l2) / 2) * 10) / 10;
}

// ---------- types for enriched team ----------

// TASK-67 PR-D: dropped player1_id/player2_id/player2_invite_email/
// player2_invite_name fields. Roster is now sourced from team_members
// (embedded via PostgREST). p1/p2 are derived from the first two
// primaries by joined_at — same canonical pair the schedule generator
// uses (sync_league_match_to_match_results trigger). hasPendingInvite
// is computed from team_invitations instead of teams.player2_invite_*.
interface EnrichedTeam {
  id: string;
  team_name: string | null;
  league_id: string;
  division_id: string | null;
  payment_status: string | null;
  status: string | null;
  created_at: string;
  /** First primary by joined_at (was teams.player1_id slot). */
  p1: { id: string; full_name: string | null; level: number | null } | null;
  /** Second primary by joined_at (was teams.player2_id slot). May be null. */
  p2: { id: string; full_name: string | null; level: number | null } | null;
  /** True when team has fewer than 2 primaries AND has at least one
   *  pending team_invitations row — surfaces the "⏳ Inväntar partner"
   *  hint that previously read teams.player2_invite_name. */
  pendingInviteName: string | null;
}

// ---------- DraggableTeamCard ----------

function DraggableTeamCard({ team, isDragOverlay, prefMap }: { team: EnrichedTeam; isDragOverlay?: boolean; prefMap?: Map<string, any> }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: team.id,
  });

  // TASK-67 PR-D: the legacy "pending partner" pill previously read
  // teams.player2_invite_name (dropped this PR). It now reflects the
  // computed pendingInviteName which is sourced from team_invitations
  // (status='pending') in the parent useQuery.
  const pendingPartner = !team.p2 && team.pendingInviteName;
  const p2Display = pendingPartner
    ? (team.pendingInviteName || "Inväntar")
    : lastName(team.p2?.full_name);
  const displayName = team.team_name || `${lastName(team.p1?.full_name)} / ${p2Display}`;
  const level = avgLevel(team.p1?.level, team.p2?.level);
  const paid = team.payment_status === "paid";

  const baseClasses =
    "flex flex-col gap-0.5 rounded-lg border bg-card p-2 text-sm transition-colors";
  const dragClasses = isDragging && !isDragOverlay ? "opacity-50" : "";
  const overlayClasses = isDragOverlay ? "shadow-xl rotate-2 scale-105" : "";

  return (
    <div
      ref={isDragOverlay ? undefined : setNodeRef}
      className={`${baseClasses} ${dragClasses} ${overlayClasses}`}
      {...(isDragOverlay ? {} : attributes)}
    >
      <div className="flex items-center gap-2">
        <span
          className="cursor-grab touch-none text-muted-foreground"
          {...(isDragOverlay ? {} : listeners)}
        >
          <GripVertical className="h-4 w-4" />
        </span>
        <span className="truncate font-medium">{displayName}</span>
        <Badge variant="outline" className="ml-auto text-xs shrink-0">
          Niv&aring; {level}
        </Badge>
        <Badge
          variant={paid ? "default" : "destructive"}
          className="text-xs shrink-0"
        >
          {paid ? "Betald" : "Obetald"}
        </Badge>
      </div>
      <div className="pl-6 text-xs text-muted-foreground">
        ({team.p1?.full_name ?? "?"} &middot; {pendingPartner ? <span className="text-amber-600">⏳ {p2Display}</span> : (team.p2?.full_name ?? "?")})
        {" "}
        ({team.p1?.level ?? "?"} + {team.p2?.level ?? "?"})
      </div>
      {(() => {
        const pref = prefMap?.get(team.id) as any;
        if (!pref) return null;
        const parts: string[] = [];
        if (pref.available_days?.length) parts.push(`📅 ${pref.available_days.join(", ")}`);
        if (pref.unavailable_days?.length) parts.push(`⚠️ Ej ${pref.unavailable_days.join(", ")}`);
        if (pref.notes) parts.push(`💬 "${pref.notes}"`);
        if (!pref.will_play_womens) parts.push("Möter ej damer");
        if (!pref.will_play_mens) parts.push("Möter ej herrar");
        if (parts.length === 0) return null;
        return <p className="text-[10px] text-muted-foreground mt-0.5 truncate pl-6">{parts.join(" · ")}</p>;
      })()}
    </div>
  );
}

// ---------- DroppableDivision ----------

function DroppableDivision({
  id,
  children,
  isEmpty,
}: {
  id: string;
  children: React.ReactNode;
  isEmpty: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[48px] rounded-md transition-colors ${
        isOver ? "ring-2 ring-primary/30 bg-primary/5" : ""
      }`}
    >
      {isOver && isEmpty ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          Sl&auml;pp h&auml;r
        </p>
      ) : (
        children
      )}
    </div>
  );
}

// ---------- schedule summary helpers ----------

type PlayScheduleBlock = { start: string; end: string; court_ids?: string[] };
type PlayScheduleMap = Record<string, PlayScheduleBlock[]>;

const dayKeyToLabel: Record<string, string> = {
  monday: "Mån",
  tuesday: "Tis",
  wednesday: "Ons",
  thursday: "Tor",
  friday: "Fre",
  saturday: "Lör",
  sunday: "Sön",
};

function formatScheduleSummary(
  schedule: PlayScheduleMap | null | undefined,
  courts: Array<{ id: string; name: string }>,
): string[] {
  if (!schedule) return [];
  const lines: string[] = [];
  for (const [dayKey, blocks] of Object.entries(schedule)) {
    if (!blocks || blocks.length === 0) continue;
    const dayLabel = dayKeyToLabel[dayKey] ?? dayKey;
    for (const b of blocks) {
      let text = `${dayLabel} ${b.start}\u2013${b.end}`;
      if (b.court_ids && b.court_ids.length > 0) {
        const courtNames = b.court_ids
          .map((cid) => courts.find((c) => c.id === cid)?.name)
          .filter(Boolean);
        if (courtNames.length > 0) {
          text += ` \u00b7 ${courtNames.join(", ")}`;
          if (courtNames.length > 1) {
            text += ` \u00b7 ${courtNames.length} matcher parallellt`;
          }
        }
      }
      lines.push(text);
    }
  }
  return lines;
}

function computePlayDays(schedule: PlayScheduleMap | null | undefined): string {
  if (!schedule) return "";
  const days = Object.entries(schedule)
    .filter(([, blocks]) => blocks && blocks.length > 0)
    .map(([dayKey]) => dayKeyToLabel[dayKey] ?? dayKey);
  return days.join(", ");
}

// ---------- main component ----------

// UPPDRAG-45: canonical lowercase English weekday keys + Swedish display
// labels. Mirrors the dayLabels map in LeagueInfoCard.tsx and the
// SHORT_LABELS_SV in lib/leaguePlayerHelpers.ts. Keep in sync if those
// move to a shared module.
const WEEKDAY_OPTIONS: { value: string; label: string }[] = [
  { value: "monday", label: "Måndag" },
  { value: "tuesday", label: "Tisdag" },
  { value: "wednesday", label: "Onsdag" },
  { value: "thursday", label: "Torsdag" },
  { value: "friday", label: "Fredag" },
  { value: "saturday", label: "Lördag" },
  { value: "sunday", label: "Söndag" },
];
// Sentinel "no day" value — Select doesn't allow empty-string options
// (Radix limitation), so we use this in the dropdown and translate
// back to NULL on save.
const NO_DAY = "__none__";

// UPPDRAG-46: per-division match-duration override picks. NULL = inherit
// the league value. Same Radix-empty-string workaround as NO_DAY above.
const INHERIT_DURATION = "__inherit__";
const MATCH_DURATION_OPTIONS: number[] = [60, 75, 90, 105, 120];

export default function AdminDivisions({ leagueId, maxTeamsPerDivision, promotionCount, relegationCount, leagueMatchDurationMinutes }: AdminDivisionsProps) {
  const { t } = useTranslation("admin");
  const queryClient = useQueryClient();
  const { courts: orgCourts } = useOrgHall();
  const activeCourts = ((orgCourts ?? []) as any[]).filter((c: any) => c.status === "active").map((c: any) => ({ id: c.id as string, name: c.name as string }));
  const [addOpen, setAddOpen] = useState(false);
  // UPPDRAG-41: dropped `level` from newDiv state — the field is no longer
  // exposed in the "Lägg till division" dialog (admins found it confusing
  // and it has no editorial meaning beyond ordering). The createDivision
  // mutation auto-computes level = max(existing) + 1 below so calendar
  // sort + color coding keep working.
  // UPPDRAG-46: match_duration_minutes added to newDiv state (null = inherit).
  const [newDiv, setNewDiv] = useState<{
    name: string;
    play_day: string;
    max_teams: string;
    promotion_count: string;
    relegation_count: string;
    match_duration_minutes: number | null;
  }>({ name: "", play_day: "", max_teams: "", promotion_count: "", relegation_count: "", match_duration_minutes: null });
  const [editDiv, setEditDiv] = useState<any>(null);
  const [activeTeam, setActiveTeam] = useState<EnrichedTeam | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // ---------- queries ----------

  const { data: divisions = [] } = useQuery({
    queryKey: ["admin-divisions", leagueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("divisions")
        .select("*")
        .eq("league_id", leagueId)
        .order("level");
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: teams = [] } = useQuery<EnrichedTeam[]>({
    queryKey: ["admin-teams", leagueId],
    queryFn: async () => {
      // TASK-67 PR-D: dropped player1_id/player2_id/player2_invite_*
      // from SELECT. Roster now sourced from team_members embed; the
      // first 2 primaries by joined_at form the canonical p1/p2 pair
      // (matches PR-A's sync_league_match_to_match_results trigger).
      const { data, error } = await supabase
        .from("teams")
        .select(`
          id, team_name, league_id, division_id, payment_status, status, created_at,
          members:team_members(user_id, customer_id, role, joined_at,
            profile:profiles!team_members_user_id_fkey(id, full_name, level),
            customer:customers!team_members_customer_id_fkey(full_name, email))
        `)
        .eq("league_id", leagueId)
        .order("created_at");
      if (error) throw error;

      const rows = (data ?? []) as unknown as Array<{
        id: string;
        team_name: string | null;
        league_id: string;
        division_id: string | null;
        payment_status: string | null;
        status: string | null;
        created_at: string;
        members: Array<{
          user_id: string | null;
          role: "primary" | "reserve";
          joined_at: string;
          profile: { id: string; full_name: string | null; level: number | null } | null;
        }> | null;
      }>;

      // TASK-67 PR-D: pending partner shown when team has < 2 primaries
      // AND a pending invite — sourced from team_invitations now (legacy
      // teams.player2_invite_* columns dropped this PR).
      const teamIds = rows.map((t) => t.id);
      const { data: invites } = teamIds.length > 0
        ? await supabase
            .from("team_invitations")
            .select("team_id, invited_email, intended_role, status")
            .in("team_id", teamIds)
            .eq("status", "pending")
        : { data: [] as Array<{ team_id: string; invited_email: string | null; intended_role: string; status: string }> };
      const pendingInviteByTeam = new Map<string, string>();
      for (const inv of invites ?? []) {
        if (inv.intended_role !== "primary") continue;
        if (!pendingInviteByTeam.has(inv.team_id)) {
          pendingInviteByTeam.set(inv.team_id, inv.invited_email ?? "Inväntar partner");
        }
      }

      return rows.map((t) => {
        // First 2 primaries by joined_at — same canonical pair the
        // schedule generator uses.
        const primaries = (t.members ?? [])
          .filter((m) => m.role === "primary")
          .sort((a, b) => a.joined_at.localeCompare(b.joined_at));
        const p1Member = primaries[0] ?? null;
        const p2Member = primaries[1] ?? null;
        return {
          id: t.id,
          team_name: t.team_name,
          league_id: t.league_id,
          division_id: t.division_id,
          payment_status: t.payment_status,
          status: t.status,
          created_at: t.created_at,
          p1: p1Member?.profile ?? null,
          p2: p2Member?.profile ?? null,
          pendingInviteName: !p2Member ? (pendingInviteByTeam.get(t.id) ?? null) : null,
        } satisfies EnrichedTeam;
      });
    },
  });

  const { data: matchCounts = {} } = useQuery({
    queryKey: ["admin-match-counts", leagueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("league_matches")
        .select("division_id")
        .eq("league_id", leagueId);
      if (error) throw error;
      const counts: Record<string, number> = {};
      (data ?? []).forEach((m) => {
        counts[m.division_id] = (counts[m.division_id] || 0) + 1;
      });
      return counts;
    },
  });

  const { data: allPreferences = [] } = useQuery({
    queryKey: ["league-team-preferences", leagueId],
    queryFn: async () => {
      const { data } = await supabase
        .from("league_team_preferences" as any)
        .select("*")
        .eq("league_id", leagueId);
      return data ?? [];
    },
    enabled: !!leagueId,
  });
  const prefMap = new Map(((allPreferences ?? []) as any[]).map(p => [p.team_id, p]));

  // ---------- mutations ----------

  // ── BUGFIX (2026-04-28): division delete previously failed for any division
  //    with generated matches. The trash button was disabled with a
  //    "Ta bort matcher först" tooltip — but there's no UI to clear matches.
  //    Admin was stuck. MCP cross-check confirmed:
  //      - league_matches.division_id FK = ON DELETE CASCADE
  //      - teams.division_id FK = ON DELETE SET NULL
  //      - league_history / standings / league_round_availability also CASCADE
  //    So the DB already handles a clean cascade — the UI just needs to
  //    surface what will be wiped, get confirmation, and then call DELETE.
  //
  //    Mutation simplification:
  //      - Dropped the manual `UPDATE teams SET division_id = NULL` step.
  //        The FK SET NULL does it for us, and the manual UPDATE was
  //        silently swallowing its own error (no `error` destructure).
  //      - Added leagueId to the invalidation keys so they actually match
  //        the queries (admin-divisions, admin-teams, admin-match-counts
  //        are all keyed `[name, leagueId]` in this component).
  const deleteDivision = useMutation({
    mutationFn: async (divisionId: string) => {
      const { error } = await supabase
        .from("divisions")
        .delete()
        .eq("id", divisionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-divisions", leagueId] });
      queryClient.invalidateQueries({ queryKey: ["admin-teams", leagueId] });
      queryClient.invalidateQueries({ queryKey: ["admin-match-counts", leagueId] });
      toast.success(t("leagues.divisions.deleted"));
    },
    onError: (e: Error) => toast.error(e.message || t("leagues.divisions.couldNotDelete")),
  });

  const createDivision = useMutation({
    mutationFn: async () => {
      // UPPDRAG-41: auto-compute level as max(existing) + 1 instead of
      // accepting admin input. Calendar (LeagueCalendar.tsx) sorts +
      // colors by division.level and labels matches "D{level}", and
      // useLeagueCalendarData orders matches by division_level — so a
      // monotonic value matters. Using existing divisions count keeps
      // the natural "Division 1, 2, 3..." ordering admins are used to.
      const nextLevel = divisions.length === 0
        ? 1
        : Math.max(...divisions.map((d: { level: number }) => d.level ?? 0)) + 1;
      const { error } = await supabase.from("divisions").insert({
        league_id: leagueId,
        name: newDiv.name,
        level: nextLevel,
        play_day: newDiv.play_day || null,
        max_teams: newDiv.max_teams ? parseInt(newDiv.max_teams) : null,
        promotion_count: newDiv.promotion_count ? parseInt(newDiv.promotion_count) : null,
        relegation_count: newDiv.relegation_count ? parseInt(newDiv.relegation_count) : null,
        // UPPDRAG-46: per-division match-duration override. NULL = inherit
        // from leagues.match_duration_minutes downstream.
        match_duration_minutes: newDiv.match_duration_minutes,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-divisions", leagueId] });
      setAddOpen(false);
      // UPPDRAG-41: dropped `level` from reset (no longer in state).
      // UPPDRAG-46: match_duration_minutes resets to null (inherit).
      setNewDiv({ name: "", play_day: "", max_teams: "", promotion_count: "", relegation_count: "", match_duration_minutes: null });
      toast.success(t("leagues.divisions.created"));
    },
    onError: (err: Error) => {
      console.error(err);
      toast.error(err?.message || t("leagues.divisions.couldNotCreate"));
    },
  });

  const updateDivision = useMutation({
    mutationFn: async () => {
      if (!editDiv) return;
      // UPPDRAG-41: dropped `level` from the update payload — the field
      // is no longer editable in the dialog. Existing values are
      // preserved as-is (no overwrite), which is what admins expect when
      // they edit name / play_day / max_teams etc.
      const { error } = await supabase.from("divisions").update({
        name: editDiv.name,
        play_day: editDiv.play_day || null,
        max_teams: editDiv.max_teams || null,
        promotion_count: editDiv.promotion_count || null,
        relegation_count: editDiv.relegation_count || null,
        // UPPDRAG-46: per-division match-duration override. NULL = inherit
        // from leagues.match_duration_minutes downstream. editDiv exposes
        // the field as `number | null` (initialised from the row on open).
        match_duration_minutes: editDiv.match_duration_minutes ?? null,
      } as any).eq("id", editDiv.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-divisions", leagueId] });
      setEditDiv(null);
      toast.success(t("leagues.divisions.updated"));
    },
    onError: (err: Error) => toast.error(err?.message || t("leagues.divisions.couldNotUpdate")),
  });

  const assignTeam = useMutation({
    mutationFn: async ({ teamId, divisionId }: { teamId: string; divisionId: string | null }) => {
      const { error } = await supabase
        .from("teams")
        .update({ division_id: divisionId })
        .eq("id", teamId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-teams", leagueId] });
      toast.success(t("leagues.divisions.teamPlaced"));
    },
  });

  const autoDistribute = useMutation({
    mutationFn: async () => {
      if (divisions.length === 0) {
        throw new Error(t("leagues.divisions.createDivisionsFirst"));
      }
      const sorted = [...teams].sort((a, b) => {
        const avgA = ((a.p1?.level ?? 1) + (a.p2?.level ?? 1)) / 2;
        const avgB = ((b.p1?.level ?? 1) + (b.p2?.level ?? 1)) / 2;
        return avgB - avgA;
      });

      const updates = sorted.map((team, i) => {
        const divIndex = Math.min(Math.floor(i / maxTeamsPerDivision), divisions.length - 1);
        return supabase
          .from("teams")
          .update({ division_id: divisions[divIndex].id })
          .eq("id", team.id);
      });

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-teams", leagueId] });
      toast.success(t("leagues.divisions.teamsDistributed"));
    },
    onError: (e: Error) => toast.error(e.message || t("leagues.divisions.couldNotDistribute")),
  });

  const suggestDivisions = useMutation({
    mutationFn: async () => {
      const count = Math.ceil(teams.length / maxTeamsPerDivision);
      const inserts = Array.from({ length: count }, (_, i) => ({
        league_id: leagueId,
        name: `Division ${i + 1}`,
        level: i + 1,
      }));
      const { error } = await supabase.from("divisions").insert(inserts);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-divisions", leagueId] });
      toast.success(t("leagues.divisions.divisionsCreated"));
    },
  });

  // ---------- drag handlers ----------

  function handleDragStart(event: DragStartEvent) {
    const team = teams.find((t) => t.id === event.active.id);
    setActiveTeam(team ?? null);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveTeam(null);
    if (!over) return;
    const teamId = active.id as string;
    const targetDivisionId = over.id === "unplaced" ? null : (over.id as string);
    const currentTeam = teams.find((t) => t.id === teamId);
    if (currentTeam?.division_id === targetDivisionId) return;
    assignTeam.mutate({ teamId, divisionId: targetDivisionId });
  }

  // ---------- derived ----------

  const unplacedTeams = teams.filter((t) => !t.division_id);

  // ---------- render ----------

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="space-y-4">
        {/* Toolbar */}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setAddOpen(true)} className="gap-1">
            <Plus className="h-4 w-4" /> {t("leagues.divisions.addDivision")}
          </Button>
          {divisions.length === 0 && teams.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => suggestDivisions.mutate()}>
              {t("leagues.divisions.suggestedDivisions")}
            </Button>
          )}
          {divisions.length > 0 && teams.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => autoDistribute.mutate()} className="gap-1">
              <Shuffle className="h-3.5 w-3.5" /> {t("leagues.divisions.distributeAll")}
            </Button>
          )}
        </div>

        {/* Unplaced teams */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">{t("leagues.divisions.teamsWithoutDivision")} ({unplacedTeams.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <DroppableDivision id="unplaced" isEmpty={unplacedTeams.length === 0}>
              <div className="space-y-2">
                {unplacedTeams.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("leagues.divisions.allTeamsPlaced")}</p>
                ) : (
                  unplacedTeams.map((team) => (
                    <DraggableTeamCard key={team.id} team={team} prefMap={prefMap} />
                  ))
                )}
              </div>
            </DroppableDivision>
          </CardContent>
        </Card>

        {/* Divisions */}
        {divisions.map((div) => {
          const divTeams = teams.filter((t) => t.division_id === div.id);
          return (
            <Card key={div.id}>
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">{div.name}</CardTitle>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {divTeams.length} / {(div as any).max_teams ?? maxTeamsPerDivision} lag
                      {(() => {
                        const playDays = computePlayDays(div.play_schedule as PlayScheduleMap | null);
                        // UPPDRAG-45: render localized label for canonical
                        // lowercase keys; fall back to raw value for any
                        // legacy/non-canonical strings that slipped through.
                        const dayLabel = div.play_day
                          ? (WEEKDAY_OPTIONS.find((o) => o.value === div.play_day)?.label ?? div.play_day)
                          : null;
                        return playDays ? ` · ${playDays}` : dayLabel ? ` · ${dayLabel}` : "";
                      })()}
                      {/* UPPDRAG-46: surface the per-division match-duration override
                          on the meta line so admins can see at a glance which divisions
                          deviate from the league default. Hidden when NULL (= inherit). */}
                      {(div as any).match_duration_minutes != null && ` · ${(div as any).match_duration_minutes} min/match`}
                      {(matchCounts[div.id] ?? 0) > 0 && ` · ${matchCounts[div.id]} matcher`}
                    </span>
                    {/* Custom promotion/relegation badge */}
                    {(((div as any).promotion_count != null && (div as any).promotion_count !== promotionCount) ||
                      ((div as any).relegation_count != null && (div as any).relegation_count !== relegationCount)) && (
                      <Badge variant="outline" className="text-[10px] px-1.5">
                        ↑{(div as any).promotion_count ?? promotionCount} ↓{(div as any).relegation_count ?? relegationCount}
                      </Badge>
                    )}
                    {/* Edit division */}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditDiv({ ...div, max_teams: (div as any).max_teams ?? "", promotion_count: (div as any).promotion_count ?? "", relegation_count: (div as any).relegation_count ?? "" })}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {/* Delete division — BUGFIX (2026-04-28):
                        Trash was disabled (with a tooltip-only hint) when the
                        division had matches, blocking admin from ever removing
                        it because there's no UI to clear matches first. Now
                        always clickable; the AlertDialog explicitly enumerates
                        the cascade so admin can make an informed choice. The
                        DB cascades (teams → SET NULL, league_matches +
                        standings + league_history + league_round_availability
                        → CASCADE) are verified via MCP. */}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Ta bort {div.name}?</AlertDialogTitle>
                          <AlertDialogDescription asChild>
                            <div className="space-y-2 text-sm">
                              <p>Denna åtgärd kan inte ångras.</p>
                              {(matchCounts[div.id] ?? 0) > 0 && (
                                <p className="text-destructive font-medium">
                                  ⚠️ {matchCounts[div.id]} {matchCounts[div.id] === 1 ? "match" : "matcher"} kommer att tas bort permanent.
                                </p>
                              )}
                              {divTeams.length > 0 && (
                                <p>
                                  {divTeams.length} {divTeams.length === 1 ? "lag flyttas" : "lag flyttas"} till &quot;Lag utan division&quot;.
                                </p>
                              )}
                            </div>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t("leagues.cancel")}</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteDivision.mutate(div.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {t("leagues.delete")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <DroppableDivision id={div.id} isEmpty={divTeams.length === 0}>
                  <div className="space-y-2">
                    {divTeams.length === 0 ? (
                      <p className="text-sm text-muted-foreground">{t("leagues.divisions.noTeamsPlaced")}</p>
                    ) : (
                      divTeams.map((team) => (
                        <DraggableTeamCard key={team.id} team={team} prefMap={prefMap} />
                      ))
                    )}
                  </div>
                </DroppableDivision>
                {/* Schedule summary */}
                {(() => {
                  const lines = formatScheduleSummary(div.play_schedule as PlayScheduleMap | null, activeCourts);
                  if (lines.length === 0) return null;
                  return (
                    <div className="mt-2 pt-2 border-t space-y-0.5">
                      {lines.map((line, i) => (
                        <div key={i} className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Clock className="h-3 w-3 shrink-0" />
                          <span>{line}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          );
        })}

        {/* Dialog: add division */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("leagues.divisions.addDivision")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Namn</Label>
                <Input value={newDiv.name} onChange={(e) => setNewDiv({ ...newDiv, name: e.target.value })} />
              </div>
              {/* UPPDRAG-41: removed Nivå input. Auto-computed as max+1 in
                  createDivision so calendar ordering keeps working. */}
              {/* UPPDRAG-45: dropdown med 7 veckodagar + "Ingen speldag".
                  Lagrar canonical lowercase English keys (monday/.../sunday)
                  i divisions.play_day; visar svenska etiketter via WEEKDAY_OPTIONS.
                  NO_DAY-sentinel mappas till NULL på save (Radix Select tillåter
                  inte tom-string-options). */}
              <div>
                <Label>Speldag (valfri)</Label>
                <Select
                  value={newDiv.play_day || NO_DAY}
                  onValueChange={(v) => setNewDiv({ ...newDiv, play_day: v === NO_DAY ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Välj speldag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_DAY}>Ingen speldag</SelectItem>
                    {WEEKDAY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* UPPDRAG-46: Matchtid-override per division. NULL = inherit
                  league value. Default option uses leagueMatchDurationMinutes
                  from props so admins see "(90 min)" on AP Nacka, "(60 min)"
                  on a league configured for shorter matches, etc. */}
              <div>
                <Label>Matchtid</Label>
                <Select
                  value={newDiv.match_duration_minutes == null ? INHERIT_DURATION : String(newDiv.match_duration_minutes)}
                  onValueChange={(v) => setNewDiv({
                    ...newDiv,
                    match_duration_minutes: v === INHERIT_DURATION ? null : parseInt(v, 10),
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={INHERIT_DURATION}>
                      Använd seriens inställning ({leagueMatchDurationMinutes} min)
                    </SelectItem>
                    {MATCH_DURATION_OPTIONS.map((m) => (
                      <SelectItem key={m} value={String(m)}>{m} min</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Max lag (valfritt)</Label>
                <Input
                  value={newDiv.max_teams}
                  onChange={(e) => setNewDiv({ ...newDiv, max_teams: e.target.value })}
                  placeholder={String(maxTeamsPerDivision)}
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">Lämna tomt = omgångens standard ({maxTeamsPerDivision} lag)</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Uppflyttning</Label>
                  <Input
                    value={newDiv.promotion_count}
                    onChange={(e) => setNewDiv({ ...newDiv, promotion_count: e.target.value })}
                    placeholder={String(promotionCount)}
                  />
                </div>
                <div>
                  <Label>Nedflyttning</Label>
                  <Input
                    value={newDiv.relegation_count}
                    onChange={(e) => setNewDiv({ ...newDiv, relegation_count: e.target.value })}
                    placeholder={String(relegationCount)}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => createDivision.mutate()} disabled={!newDiv.name}>
                Skapa
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit division dialog */}
        <Dialog open={!!editDiv} onOpenChange={(v) => { if (!v) setEditDiv(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Redigera division</DialogTitle>
            </DialogHeader>
            {editDiv && (
              <div className="space-y-3">
                <div>
                  <Label>Namn</Label>
                  <Input value={editDiv.name} onChange={(e) => setEditDiv({ ...editDiv, name: e.target.value })} />
                </div>
                {/* UPPDRAG-41: removed Nivå input. Existing value preserved
                    on save (updateDivision no longer writes the column). */}
                {/* UPPDRAG-45: same dropdown pattern in the edit dialog.
                    Existing value (a canonical key after migration 20260502130000)
                    pre-selects naturally; legacy free-form text rows would
                    show the placeholder until the admin picks a value. */}
                <div>
                  <Label>Speldag (valfri)</Label>
                  <Select
                    value={editDiv.play_day || NO_DAY}
                    onValueChange={(v) => setEditDiv({ ...editDiv, play_day: v === NO_DAY ? null : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Välj speldag" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_DAY}>Ingen speldag</SelectItem>
                      {WEEKDAY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* UPPDRAG-46: Matchtid-override per division — same picker as
                    in the create dialog. Pre-selects existing override or
                    "Använd seriens inställning" when NULL. */}
                <div>
                  <Label>Matchtid</Label>
                  <Select
                    value={editDiv.match_duration_minutes == null ? INHERIT_DURATION : String(editDiv.match_duration_minutes)}
                    onValueChange={(v) => setEditDiv({
                      ...editDiv,
                      match_duration_minutes: v === INHERIT_DURATION ? null : parseInt(v, 10),
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={INHERIT_DURATION}>
                        Använd seriens inställning ({leagueMatchDurationMinutes} min)
                      </SelectItem>
                      {MATCH_DURATION_OPTIONS.map((m) => (
                        <SelectItem key={m} value={String(m)}>{m} min</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Max lag</Label>
                  <Input value={editDiv.max_teams ?? ""} onChange={(e) => setEditDiv({ ...editDiv, max_teams: e.target.value ? parseInt(e.target.value) : null })} placeholder={String(maxTeamsPerDivision)} />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Lämna tomt = omgångens standard ({maxTeamsPerDivision} lag)</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Uppflyttning</Label>
                    <Input value={editDiv.promotion_count ?? ""} onChange={(e) => setEditDiv({ ...editDiv, promotion_count: e.target.value ? parseInt(e.target.value) : null })} placeholder={String(promotionCount)} />
                  </div>
                  <div>
                    <Label>Nedflyttning</Label>
                    <Input value={editDiv.relegation_count ?? ""} onChange={(e) => setEditDiv({ ...editDiv, relegation_count: e.target.value ? parseInt(e.target.value) : null })} placeholder={String(relegationCount)} />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDiv(null)}>Avbryt</Button>
              <Button onClick={() => updateDivision.mutate()} disabled={!editDiv?.name || updateDivision.isPending}>
                Spara
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* DragOverlay: ghost card */}
      <DragOverlay>
        {activeTeam ? <DraggableTeamCard team={activeTeam} isDragOverlay /> : null}
      </DragOverlay>
    </DndContext>
  );
}
